import React from 'react';
import {Document, Image, Page, StyleSheet, Text, View} from '@react-pdf/renderer';
import {InvoiceDocumentProps} from '../../../../entity/props/title/invoice-props';
import {calculateInvoiceTotal, formatCurrency} from '../../../../shared/report/currency-utils';
import {formatDueDate, getCurrentDate} from '../../../../shared/report/date-utils';

const InvoiceDocument = (props: InvoiceDocumentProps): JSX.Element => {
  const {values, template} = props;

  const styles = StyleSheet.create({
    page: {
      flexDirection: 'column',
      backgroundColor: '#ffffff',
      padding: 40,
      fontSize: 10
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 30,
      alignItems: 'flex-start'
    },
    companyInfo: {
      flex: 1
    },
    companyName: {
      fontSize: 20,
      fontWeight: 'bold',
      color: '#e02b35',
      marginBottom: 10,
      marginLeft: 0
    },
    companyAddress: {
      fontSize: 10,
      color: '#000000',
      marginBottom: 5,
      lineHeight: 1.2,
      marginLeft: 0
    },
    companyContact: {
      fontSize: 10,
      color: '#000000',
      marginBottom: 3,
      marginLeft: 0
    },
    logo: {
      width: 150,
      height: 50,
      objectFit: 'contain',
      marginLeft: -50,
      marginBottom: 10
    },
    invoiceDetails: {
      alignItems: 'flex-end'
    },
    invoiceDetail: {
      fontSize: 10,
      color: '#000000',
      marginBottom: 3
    },
    billToSection: {
      marginBottom: 30
    },
    sectionTitle: {
      fontSize: 12,
      fontWeight: 'bold',
      color: '#000000',
      marginBottom: 10
    },
    clientName: {
      fontSize: 10,
      color: '#000000',
      marginBottom: 5
    },
    servicesSection: {
      marginBottom: 20
    },
    table: {
      marginTop: 10
    },
    tableHeader: {
      backgroundColor: '#f0f0f0',
      flexDirection: 'row',
      padding: 8,
      borderBottomWidth: 1,
      borderBottomColor: '#000000'
    },
    tableHeaderCell: {
      flex: 1,
      fontSize: 10,
      fontWeight: 'bold',
      color: '#000000',
      textAlign: 'left'
    },
    tableHeaderCellAmount: {
      flex: 1,
      fontSize: 10,
      fontWeight: 'bold',
      color: '#000000',
      textAlign: 'right'
    },
    tableRow: {
      flexDirection: 'row',
      padding: 8,
      borderBottomWidth: 1,
      borderBottomColor: '#e0e0e0'
    },
    tableCell: {
      flex: 1,
      fontSize: 10,
      color: '#000000',
      textAlign: 'left'
    },
    tableCellAmount: {
      flex: 1,
      fontSize: 10,
      color: '#000000',
      textAlign: 'right'
    },
    serviceDescription: {
      fontSize: 10,
      color: '#000000',
      marginBottom: 3
    },
    serviceSubtext: {
      fontSize: 9,
      color: '#666666',
      marginBottom: 5
    },
    totalSection: {
      marginTop: 20,
      borderTopWidth: 1,
      borderTopColor: '#000000',
      paddingTop: 10
    },
    totalRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: '#f8f9fa',
      padding: 10
    },
    totalLabel: {
      fontSize: 12,
      fontWeight: 'bold',
      color: '#000000'
    },
    totalAmount: {
      fontSize: 12,
      fontWeight: 'bold',
      color: '#000000'
    },
    notesSection: {
      marginTop: 30
    },
    notesText: {
      fontSize: 10,
      color: '#000000',
      lineHeight: 1.4
    },
    footer: {
      position: 'absolute',
      bottom: 50,
      left: 40,
      right: 40,
      textAlign: 'center'
    },
    footerText: {
      fontSize: 10,
      color: '#000000'
    }
  });

  const today = getCurrentDate();
  const dueDateFormatted = formatDueDate(values.dueDate);
  const totalAmount = calculateInvoiceTotal(values.fee, values.receiptsCopyCosts);

  return (
    <Document>
      <Page size='A4' style={styles.page}>
        <View style={styles.header}>
          <View style={styles.companyInfo}>
            {
              template.logo &&
                <Image src={template.logo} style={styles.logo}/>
            }
            <Text style={styles.companyName}>{template.companyName}</Text>

            {
              template.companyAddress.split('\n').map((line, index) => <Text key={index} style={styles.companyAddress}>{line}</Text>)
            }
            {
              template.phone &&
                <Text style={styles.companyContact}>Phone: {template.phone}</Text>
            }
            {
              template.email &&
                <Text style={styles.companyContact}>Email: {template.email}</Text>
            }
            {
              template.website &&
                <Text style={styles.companyContact}>Website: {template.website}</Text>
            }
          </View>
          <View style={styles.invoiceDetails}>
            <Text style={styles.invoiceDetail}>Invoice #: {values.invoiceNumber}</Text>
            <Text style={styles.invoiceDetail}>Date: {today}</Text>
            <Text style={styles.invoiceDetail}>Due Date: {dueDateFormatted}</Text>
          </View>
        </View>
        <View style={styles.billToSection}>
          <Text style={styles.sectionTitle}>Bill To:</Text>
          <Text style={styles.clientName}>{values.clientName}</Text>
        </View>
        <View style={styles.servicesSection}>
          <Text style={styles.sectionTitle}>Services:</Text>
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={styles.tableHeaderCell}>Description</Text>
              <Text style={styles.tableHeaderCellAmount}>Amount</Text>
            </View>
            <View style={styles.tableRow}>
              <View style={{flex: 1}}>
                <Text style={styles.serviceDescription}>
                  Title Search Services - Order {values.orderNumber}
                </Text>
                <Text style={styles.serviceSubtext}>
                  {values.propertyAddress}
                </Text>
              </View>
              <Text style={styles.tableCellAmount}>
                {formatCurrency(values.fee || '0')}
              </Text>
            </View>
            {
              values.receiptsCopyCosts && parseFloat(values.receiptsCopyCosts) > 0 &&
                <View style={styles.tableRow}>
                  <Text style={styles.tableCell}>Receipts / Copy Costs</Text>
                  <Text style={styles.tableCellAmount}>
                    {formatCurrency(values.receiptsCopyCosts)}
                  </Text>
                </View>
            }
          </View>
        </View>
        <View style={styles.totalSection}>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Total:</Text>
            <Text style={styles.totalAmount}>{formatCurrency(totalAmount)}</Text>
          </View>
        </View>
        {
          values.notes &&
            <View style={styles.notesSection}>
              <Text style={styles.sectionTitle}>Notes:</Text>
              <Text style={styles.notesText}>{values.notes}</Text>
            </View>
        }
        <View style={styles.footer}>
          <Text style={styles.footerText}>{template.footerText}</Text>
        </View>
      </Page>
    </Document>
  );
}

export default InvoiceDocument; 